import {
  APISurveyReferenceComponent,
  ArdoqId,
  PersistedReferenceQuestion,
} from '@ardoq/api-types';
import { WithPopover } from '@ardoq/popovers';
import { SelectOption } from '@ardoq/select';
import styled from 'styled-components';
import { Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  FetchComponentOptionsWithQuery,
  Fields,
  GetAISuggestions,
  ReferenceAnswer,
  ReferenceAnswerValue,
} from '../../types';
import { GetUserFeedbackReturn } from '../getUserFeedback';
import { SurveyQuestionOptions } from '../QuestionOptions/SurveyQuestionOptions';
import { SurveyReadOnlyQuestion } from '../SurveyReadOnlyQuestion';
import { namePathToString } from '../../utils/stringUtils';
import { createReferenceFromOption } from './referenceQuestionUtils';
import { QuestionContentWrapper } from '../questionsTypes';

const Popover = styled.div`
  width: fit-content;
  max-width: 400px;
`;

const Wrapper = styled.div`
  padding-bottom: 24px;
`;

type SurveySingleSelectReferenceQuestionProps = {
  question: PersistedReferenceQuestion;
  answer: ReferenceAnswer | undefined;
  selectedComponent: APISurveyReferenceComponent | undefined;
  readonly: boolean;
  surveyId: ArdoqId;
  questionId: ArdoqId;
  componentId: ArdoqId | undefined;
  userFeedback: GetUserFeedbackReturn;
  fields: Fields;
  identifierFieldName: string | undefined;
  shouldFetchReferenceSuggestions: boolean;
  allowComponentCreation: boolean | undefined;
  outgoing: boolean | undefined;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  hideReadOnlyLabel: boolean;
  questionLabel: string;
  highlightUnansweredPriorityQuestions: boolean;
  setAnswer: (answer: {
    questionId: string;
    value: ReferenceAnswerValue[];
  }) => void;
  additionalFilterOptions: (option: SelectOption<string>) => boolean;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  QuestionContentWrapper: QuestionContentWrapper;
};

export const SurveySingleSelectReferenceQuestion = ({
  question,
  answer,
  selectedComponent,
  readonly,
  surveyId,
  questionId,
  componentId,
  userFeedback,
  fields,
  identifierFieldName,
  shouldFetchReferenceSuggestions,
  allowComponentCreation,
  outgoing,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hideReadOnlyLabel,
  questionLabel,
  highlightUnansweredPriorityQuestions,
  setAnswer,
  additionalFilterOptions,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  QuestionContentWrapper,
}: SurveySingleSelectReferenceQuestionProps) => {
  const selectedOption = selectedComponent
    ? {
        label: (
          <WithPopover
            content={() => (
              <Popover>
                {selectedComponent.namePath
                  ? namePathToString(selectedComponent.namePath)
                  : selectedComponent.name}
              </Popover>
            )}
          >
            <span>{selectedComponent.name}</span>
          </WithPopover>
        ),
        value: selectedComponent._id || selectedComponent.name,
      }
    : undefined;

  if (readonly) {
    return (
      <QuestionContentWrapper
        question={question}
        answer={answer}
        highlightUnansweredPriorityQuestions={
          highlightUnansweredPriorityQuestions
        }
        i18nLocale={i18nLocale}
      >
        <SurveyReadOnlyQuestion
          hideReadOnlyLabel={hideReadOnlyLabel}
          i18nLocale={i18nLocale}
        >
          {selectedOption
            ? selectedOption.label
            : t(i18nLocale, 'surveysUi:noneSelected', 'None selected.')}
        </SurveyReadOnlyQuestion>
      </QuestionContentWrapper>
    );
  }
  return (
    <QuestionContentWrapper
      question={question}
      answer={answer}
      highlightUnansweredPriorityQuestions={
        highlightUnansweredPriorityQuestions
      }
      i18nLocale={i18nLocale}
    >
      <Wrapper>
        <SurveyQuestionOptions
          onChange={(
            refOption: SelectOption<string> | SelectOption<string>[] | null
          ) => {
            if (Array.isArray(refOption)) return;

            const referenceValue = refOption
              ? [createReferenceFromOption(refOption, outgoing)]
              : [];
            return setAnswer({ questionId, value: referenceValue });
          }}
          isCreatable={allowComponentCreation}
          value={selectedOption}
          currentLocale={currentLocale}
          i18nLocale={i18nLocale}
          surveyId={surveyId}
          userFeedback={userFeedback}
          questionId={questionId}
          componentId={componentId}
          identifierFieldName={identifierFieldName}
          fields={fields}
          questionLabel={questionLabel}
          shouldFetchReferenceSuggestions={shouldFetchReferenceSuggestions}
          additionalFilterOptions={additionalFilterOptions}
          getAISuggestions={getAISuggestions}
          fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
        />
      </Wrapper>
    </QuestionContentWrapper>
  );
};
